import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnChanges,
  OnInit,
  output,
  Signal,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ActionButton } from '@shared/abstraction/grid/action-button';
import { SearchSetting } from '@shared/abstraction/grid/search-setting';
import {
  DrawerParams,
  DrawerSetting,
  GridColumn,
  PrimeGridOption,
} from '@shared/modules/grid/abstractions';
import { PrimeGridComponent } from '@shared/modules/grid/components/prime-grid/prime-grid.component';
import { QueryOptions } from 'odata-query';
import { Subscription } from 'rxjs';
import {
  Collection,
  GridItemCreateEvent,
  GridItemUpdateEvent,
} from '../../models';

@Component({
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'gstar-admin-prime-grid',
  templateUrl: './admin-prime-grid.component.html',
})
export class AdminPrimeGridComponent<TData, TDataOutput>
  implements OnChanges, OnInit
{
  private translateService = inject(TranslateService);

  source: InputSignal<Collection<TData>> = input.required();
  headers: InputSignal<GridColumn[]> = input.required();
  query: InputSignal<Partial<QueryOptions<TData>>> = input.required();
  searchSetting: InputSignal<SearchSetting<TData> | null> =
    input<SearchSetting<TData> | null>(null);
  drawerSetting: InputSignal<DrawerSetting<TData, TDataOutput>> =
    input.required();
  normalActionMenu: InputSignal<ActionButton<TData>[]> = input.required();
  actionMenuTitle: InputSignal<string> = input('');
  showSearch: InputSignal<boolean> = input(true);
  showDrawer: InputSignal<boolean> = input(true);
  disableSelectAllModal: InputSignal<boolean> = input(false);
  gridSetting: InputSignal<Partial<PrimeGridOption<TData>>> = input({});

  queryChange = output<Partial<QueryOptions<TData>>>();
  create = output<GridItemCreateEvent<TData>>();
  update = output<GridItemUpdateEvent<TData>>();
  selectRows = output<TData[]>();
  grid: Signal<PrimeGridComponent<TData> | undefined> =
    viewChild<PrimeGridComponent<TData>>('grid');
  queryWritable: WritableSignal<Partial<QueryOptions<TData>>> = signal({});
  drawerSettingWritable: WritableSignal<DrawerSetting<TData, TDataOutput>> =
    signal({});

  setting: PrimeGridOption<TData> = {
    lazy: true,
    hasCheckbox: false,
    emptyMessage: this.translateService.instant(
      'admin.dashboard.notFoundRecordsMessage'
    ),
    collapsableRows: [],
    isAccordionEnabled: false,
    isExpandedByDefault: false,
    actionMenu: (): ActionButton<TData>[] => this.normalActionMenu(),
  };

  isLoading: WritableSignal<boolean> = signal(true);

  selectedDrawerType: DrawerParams<TData, TDataOutput> | undefined;
  drawerModelChangeEmitter = new EventEmitter<{
    model: TDataOutput;
    prevModel: TDataOutput;
    isValid: boolean;
  }>();

  drawerModelChangeSubscription!: Subscription;

  selectedDrawerData:
    | {
        model: TDataOutput;
        prevModel: TDataOutput;
        isValid: boolean;
      }
    | undefined;

  constructor() {
    this.drawerModelChangeSubscription =
      this.drawerModelChangeEmitter.subscribe(changes => {
        this.selectedDrawerData = changes;
      });
  }

  ngOnInit(): void {
    this.queryWritable.set(this.query());
    this.drawerSettingWritable.set(this.drawerSetting());
    this.setting.actionMenuTitle = this.actionMenuTitle();
    this.setting.disableSelectAllModal = this.disableSelectAllModal();
    // Merge gridSetting with default setting
    Object.assign(this.setting, this.gridSetting());
  }

  ngOnChanges(): void {
    if (this.source().total > 0) {
      this.isLoading.set(false);
    }
  }

  queryHandler(securityTypeQuery: Partial<QueryOptions<TData>>): void {
    this.queryWritable.update((previousQuery: Partial<QueryOptions<TData>>) => {
      return {
        ...previousQuery,
        ...securityTypeQuery,
      };
    });
    this.queryChange.emit(this.queryWritable());
  }

  hasFilterableHeaders(): boolean {
    return this.headers().some((header: GridColumn) => !!header.isFilterable);
  }

  search(event: { originEvent: unknown; queryStr: string }): void {
    const searchSetting = this.searchSetting();
    if (!searchSetting) return;

    const queryStr = event.queryStr?.trim() ?? '';

    this.queryWritable.update((previousQuery: Partial<QueryOptions<TData>>) => {
      // Empty search clears filter and returns full dataset.
      if (queryStr.length === 0) {
        return {
          ...previousQuery,
          skip: 0,
          filter: undefined,
        };
      }

      // Require at least 3 characters to apply search filters.
      if (queryStr.length < 3) {
        return {
          ...previousQuery,
          skip: 0,
          filter: undefined,
        };
      }

      return {
        ...previousQuery,
        skip: 0,
        filter: {
          [searchSetting.filtersLogicalOperator]: searchSetting.filters.map(
            filter => ({
              [filter.dataKey]: {
                [filter.operator]: queryStr,
              },
            })
          ),
        },
      };
    });
    this.grid()?.goToPage(1);
  }

  createActionClick(): void {
    const createConfig = this.drawerSettingWritable().create;
    if (createConfig) {
      this.selectedDrawerData = undefined;
      this.selectedDrawerType = {
        ...createConfig,
        componentInputs: {
          modelChange: this.drawerModelChangeEmitter,
          isEdit: false,
          isBulkEdit: false,
          bulkItems: [],
        },
      };
    }
  }

  editActionClick(row: TData, extraInputs?: Record<string, unknown>): void {
    const editConfig = this.drawerSettingWritable().edit;
    if (editConfig) {
      const drawerTitleOverride = extraInputs?.['drawerTitleOverride'] as
        | string
        | undefined;
      const primaryLabelOverride = extraInputs?.['primaryLabelOverride'] as
        | string
        | undefined;
      const {
        drawerTitleOverride: _drawerTitleOverride,
        primaryLabelOverride: _primaryLabelOverride,
        ...componentExtraInputs
      } = extraInputs ?? {};

      // Reset stale state from previous open drawer edit before loading new row.
      this.selectedDrawerData = undefined;
      this.selectedDrawerType = {
        ...editConfig,
        title: drawerTitleOverride ?? editConfig.title,
        buttons: editConfig.buttons.map(button =>
          button.isPrimary && primaryLabelOverride
            ? { ...button, label: primaryLabelOverride }
            : button
        ),
        componentInputs: {
          model: row,
          modelChange: this.drawerModelChangeEmitter,
          isEdit: true,
          isBulkEdit: false,
          bulkItems: [],
          ...componentExtraInputs,
        },
      };
    }
  }

  drawerCloseClick(): void {
    this.selectedDrawerType = undefined;
    this.selectedDrawerData = undefined;
  }

  clearSelection(): void {
    this.grid()?.clearSelection();
  }
}
